/**
 * Provider-agnostic browser softphone contract. The dashboard and the extension both
 * consume the same `Softphone` UI + `useSoftphone` hook; only the concrete client
 * (Telnyx / Twilio / JustCall) and the injected mic strategy differ per surface.
 */

export type SoftphoneStatus = 'idle' | 'connecting' | 'ringing' | 'active' | 'ended' | 'error';

/**
 * The carriers the browser softphone can speak to. A local copy of the shared list in
 * `@poseidon/types/svr/voiceProvider` — UI components may not import svr types; the app wrappers'
 * values assign structurally.
 */
export type SoftphoneProvider = 'telnyx' | 'twilio' | 'justcall';

/**
 * SIP custom header that carries our `voice_call.id` on the outbound call so the server
 * webhook can correlate back to the row. MUST match the server constant in
 * `packages/server/src/api/services/voice/voiceProvider.types.ts`.
 */
export const VOICE_CALL_ID_HEADER = 'X-Voice-Call-Id';

/**
 * The carrier refused to place the call (it ended before it ever rang, and the server's status
 * callback says `failed`). `code` is the carrier's own error number — Twilio 13225 "call blocked by
 * Twilio" is the account-level block the notice is built around — or null when the carrier filed
 * no code. Distinct from the client-side `error` status: the WebRTC leg was fine, the PSTN bridge
 * was rejected, which the browser otherwise can't tell apart from "nobody answered".
 */
export interface SoftphoneCarrierFailure {
  code: number | null;
}

/** How the carrier settled a call, as the server recorded it from the status callback. */
export interface SoftphoneCallResult {
  /** Normalized terminal status ('completed' | 'no_answer' | 'busy' | 'failed' | 'canceled'), or null until the callback lands. */
  callStatus: string | null;
  carrierErrorCode: number | null;
  /**
   * The disposition the server derived on its own from the carrier verdict / answering-machine
   * detection / talk time (an outcome key, e.g. 'no_answer', 'voicemail', 'connected'), or null
   * when the call is ambiguous and the rep must pick. Optional so an older server is still valid.
   */
  autoOutcome?: string | null;
}

/**
 * How a call ended, in the carrier's own terms — the detail that explains a failure but has no
 * place in the UI. Emitted for every terminal call event and every device-level failure.
 *
 * This exists because the codes that diagnose a broken call (Twilio 31000 TwiML fetch failure,
 * 13214 invalid caller id surfacing as an instant hangup) were previously written to
 * `console.error` and nowhere else: the browser console is not collected, so a production
 * calling outage left no trace on any server and had to be reconstructed from carrier logs by
 * hand. The surface supplies the sink — the dashboard reports to Sentry, the extension can log
 * or ignore — so this package stays free of any reporter dependency.
 *
 * Deliberately separate from `SoftphoneCarrierFailure` above, which is the REP-facing story and
 * comes from the server's status callback. This one is the ENGINEER-facing story and comes from
 * the browser SDK, so it still fires for the failures that never reach a status callback at all —
 * a `<Dial>` the carrier rejects outright kills the browser leg before any call leg exists, which
 * is exactly the case that left no server-side trace.
 */
export interface SoftphoneDiagnostic {
  /** The terminal call event, or `device-error` for a failure on the signaling client itself. */
  event: 'disconnect' | 'cancel' | 'reject' | 'error' | 'device-error';
  /** Our correlation id for the call, when one exists (never set for device-level errors). */
  voiceCallId: string | null;
  /**
   * Whether the call was ever answered. A terminal event with this false is the instant-hangup
   * signature: the call died before media, which is what a rejected `<Dial>` looks like in the
   * browser.
   */
  becameActive: boolean;
  /**
   * Whether this is worth reporting: an `error` event, any carrier code, or a call that ended
   * without ever becoming active. A normal hangup after a real conversation is false.
   */
  abnormal: boolean;
  /** Carrier error code when the SDK supplied one (e.g. Twilio 31000). */
  code?: number;
  description?: string;
  explanation?: string;
  message?: string;
}

export interface SoftphoneCallHandlers {
  onStatus: (status: SoftphoneStatus) => void;
  onError: (message: string) => void;
  /**
   * Optional diagnostic sink (see `SoftphoneDiagnostic`). Optional so a surface that has nowhere
   * to send them simply omits it rather than passing a no-op.
   */
  onDiagnostic?: (diagnostic: SoftphoneDiagnostic) => void;
  /**
   * A short instruction for the rep while a call is being set up (e.g. "Sign in to JustCall…"),
   * or null to clear it. Only clients that need the rep's help to place a call emit these.
   */
  onNotice?: (notice: string | null) => void;
  /**
   * The carrier's own id for the call being placed, once it's known (`ended: false`) and again
   * when the call ends (`ended: true`). Only emitted by clients whose carrier can't carry our
   * correlation id (JustCall) — the surface forwards it to `voiceCall.reportProviderCall` so the
   * server can pull the call's result itself.
   */
  onProviderCall?: (event: { providerCallId: string; ended: boolean }) => void;
}

export interface SoftphoneDialParams {
  destinationNumber: string;
  callerNumber: string | null;
  voiceCallId: string;
}

/**
 * Platform mic capability — the injectable seam. The dashboard leaves this undefined and
 * lets the SDK call `getUserMedia` directly; the extension supplies a stream captured in
 * its offscreen document (where a persistent mic is allowed under MV3).
 */
export interface SoftphoneMediaStrategy {
  getInputStream?: () => Promise<MediaStream>;
}

export interface SoftphoneClient {
  /**
   * True when the carrier's own dialer runs the call (JustCall's embedded iframe): mute, keypad
   * and hang-up live there, so the dialer UI hides ours and `hangup`/`setMuted`/`sendDigits` do
   * nothing. Absent / false for carriers we drive directly (Twilio, Telnyx).
   */
  readonly embeddedCarrierDialer?: boolean;
  /** Register lifecycle handlers. Call before `init`/`dial`. */
  on(handlers: SoftphoneCallHandlers): void;
  /** Connect the signaling client with a freshly minted token. */
  init(token: string): Promise<void>;
  /** Place the outbound call. */
  dial(params: SoftphoneDialParams): Promise<void>;
  hangup(): void;
  setMuted(muted: boolean): void;
  /**
   * Send DTMF tones on the live call (IVR navigation). Accepts `0-9`, `*`, `#`.
   * No-op when no call is active (parity with hangup/setMuted). May throw on providers that
   * reject digits before media is established — callers guard with try/catch.
   */
  sendDigits(digits: string): void;
  /**
   * End the current call and release its per-call resources (disconnect, detach the call's
   * listeners, clear the active call) WITHOUT tearing down the signaling client/device — so the
   * next dial reuses the already-registered connection. Idempotent; safe with no active call.
   */
  closeCall(): void;
  /** Tear down the call, signaling connection, and any DOM/audio resources. */
  destroy(): Promise<void>;
}
